import { useEffect, useRef, useState } from 'react';
import type { ConsoleView } from './MobileNav.tsx';

/**
 * Decide the brand banner's next hidden state from a scroll position: shown at
 * the very top, hidden on a downward scroll, revealed on an upward one (with an
 * 8px deadband so jitter alone can't toggle it). Pure, so the threshold logic is
 * unit-testable without a DOM.
 */
export function nextBannerHidden(y: number, lastY: number, current: boolean): boolean {
  if (y < 8) {
    return false;
  }
  if (y > lastY + 8) {
    return true;
  }
  if (y < lastY - 8) {
    return false;
  }
  return current;
}

/**
 * Owns the mobile brand-banner reveal-on-scroll for the whole console. A
 * capture-phase scroll listener watches WHICHEVER panel is scrolling (Chat,
 * Sessions, Memory, Tools, Files) — scroll doesn't bubble, but capture-phase
 * ancestors still see it — so every tab brings the OculusMind.AI banner back on
 * pull-down, not just Chat. Mobile-only: `topbar-hidden` is a mobile layout
 * rule, so this is a no-op on desktop. Switching tabs (`view`) re-shows the
 * banner and resets the scroll baseline. The 300ms cooldown rides out the scroll
 * churn the grid-row collapse animation feeds back, which would otherwise
 * oscillate the header.
 *
 * Returns whether the banner should currently be hidden.
 */
export function useBannerReveal(view: ConsoleView): boolean {
  const [bannerHidden, setBannerHidden] = useState(false);
  const hiddenRef = useRef(false);
  const lastYRef = useRef(0);
  const cooldownRef = useRef(0);

  useEffect(() => {
    const onScroll = (event: Event): void => {
      if (!window.matchMedia('(max-width: 960px)').matches) {
        return;
      }
      const el = event.target as HTMLElement | null;
      if (!el || typeof el.scrollTop !== 'number') {
        return;
      }
      const y = el.scrollTop;
      if (performance.now() < cooldownRef.current) {
        lastYRef.current = y;
        return;
      }
      const next = nextBannerHidden(y, lastYRef.current, hiddenRef.current);
      lastYRef.current = y;
      if (next !== hiddenRef.current) {
        hiddenRef.current = next;
        cooldownRef.current = performance.now() + 300;
        setBannerHidden(next);
      }
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, []);

  // Switching tabs reveals the banner and resets the baseline, so each tab opens
  // with the banner shown and the next pull-down behaves consistently.
  useEffect(() => {
    hiddenRef.current = false;
    lastYRef.current = 0;
    setBannerHidden(false);
  }, [view]);

  return bannerHidden;
}
